import { z } from 'zod';

import { Component } from './Component';

import type { FileUpload } from '../types/File';
import type { ProjectDesign } from '../types/ProjectDesign';
import type { Subscriber } from '../types/Subscription';
import type { TeamInfo } from '../types/Team';
import type { User } from '../types/User';

export const OrderAvailabilities = ['inStock', 'preorder', 'backorder', 'outOfStock'] as const;

export const OrderAvailabilityOptions = [
    { label: 'In stock', value: 'inStock' },
    { label: 'Preorder', value: 'preorder' },
    { label: 'Backorder', value: 'backorder' },
    { label: 'Out of stock', value: 'outOfStock' },
] as const;

export const OrderStatusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'quoteRequested', label: 'Quote requested' },
    { value: 'quoteSubmitted', label: 'Quote received' },
    { value: 'orderSubmitted', label: 'Order submitted' },
    { value: 'orderAcknowledged', label: 'Order acknowledged' },
    { value: 'orderCancelled', label: 'Order cancelled' },
    { value: 'orderInProgress', label: 'Order in progress' },
    { value: 'orderCompleted', label: 'Order completed' },
    { value: 'archived', label: 'Archived' },
    { value: 'deleted', label: 'Deleted' },
] as const;

export const OrderStatusSchema = z.enum([
    'draft',
    'quoteRequested',
    'quoteSubmitted',
    'orderSubmitted',
    'orderAcknowledged',
    'orderCancelled',
    'orderInProgress',
    'orderCompleted',
    'archived',
    'deleted',
]);

export const OrderStatuses = OrderStatusOptions.map((option) => option.value);

export type OrderAvailability = (typeof OrderAvailabilities)[number];

export type OrderStatus = (typeof OrderStatusOptions)[number]['value'];

export type OrderPermissions = 'order.all' | 'order.review';

export type Order = {
    id: string;
    name: string;
    design?: string | ProjectDesign;
    components?: {
        component: string | Component;
        quantity: number;
        price?: number;
        leadTime?: number;
        availability?: OrderAvailability;
        notes?: string;
    }[];
    events: {
        type: OrderStatus;
        date: string;
        user?: string | User;
    }[];
    status: OrderStatus;
    createdAt: Date;
    createdBy: User;
    messages?: {
        message: string;
        date: string;
        user: string | User;
        id?: string;
    }[];
    totalPrice?: number;
    files?: FileUpload[];
    team: TeamInfo;
    subscribers?: Subscriber[];
    reviewers?: {
        manufacturers: string[];
    };
    permissions?: OrderPermissions[];
};

export type OrderComponentUpdate = Omit<Partial<Required<Order>['components'][number]>, 'component'>;
